import { CardNumberElement } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { Component } from 'react';

import Loader from 'hb-react/shared/components/Loader';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { numberWithTwoDecimals } from 'hb-react/shared/utils/formatNumber';

import SplitedCardFields from './SplitedCardFields';

import style from '../../index.module.scss';

class PaymentForm extends Component {
  state = {
    error: null,
    loading: false,
  };

  handlePaymentSuccess = (request) => {
    const { onPaymentSuccess } = this.props;

    if (onPaymentSuccess) {
      onPaymentSuccess(request.data);
    }

    this.contentPreview?.portal?.closePortal();
  };

  confirmPaymentOnServer = ({ paymentIntentId, response }) => {
    const { endpointUrl, endpointData } = this.props;
    const scaPayload = response.data;

    this.setState({ error: null, loading: true });
    // TODO: Rework necessary, passing gateway and transaction token twice for legacy SP and OM::CartsController compatibility
    axios
      .post(endpointUrl, {
        ...scaPayload,
        ...endpointData,
        transaction_token: paymentIntentId,
        gateway: 'Stripe',
        billing: {
          transaction_token: paymentIntentId,
          gateway: 'Stripe',
        },
      })
      .then(this.handlePaymentSuccess)
      .catch((error) => {
        if (error?.response?.data?.errors)
          this.setState({ error: error.response.data.errors });
        else if (error.message) this.setState({ error: error.message });
      })
      .then(() => {
        this.setState({ loading: false });
      });
  };

  handleServerPaymentIntentionResponse = (response) => {
    const { stripeAccountId } = this.props;
    const { data } = response;

    const stripe = window.Stripe(window.__HB_ENV__.STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: stripeAccountId,
    });

    if (data.requires_customer_additional_action) {
      stripe
        .handleCardAction(data.payment_intent_client_secret)
        .then((result) => {
          if (result.error) {
            this.setState({ loading: false, error: result.error.message });
          } else {
            this.confirmPaymentOnServer({
              paymentIntentId: result.paymentIntent.id,
              response,
            });
          }
        });
    } else {
      this.handlePaymentSuccess(response);
    }
  };

  createServerPaymentIntation = (result) => {
    const { endpointUrl, endpointData } = this.props;

    const stripePayload = {
      transaction_token: result.paymentMethod.id,
      gateway: 'Stripe',
      payment_method: 'card',
    };

    const data =
      endpointData instanceof Function
        ? endpointData(stripePayload)
        : {
            ...endpointData,
            ...stripePayload,
          };

    axios
      .post(endpointUrl, data)
      .then(this.handleServerPaymentIntentionResponse)
      .catch((error) => {
        this.setState({ loading: false });
        if (error?.response?.data?.errors)
          this.setState({ error: error.response.data.errors });
        else if (error.message) this.setState({ error: error.message });
      });
  };

  handleSubmit = (event) => {
    const { stripe, elements, userName, userEmail } = this.props;
    const { loading } = this.state;

    event.preventDefault();

    if (loading) return;

    this.setState({ error: null, loading: true });

    stripe
      .createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: { name: userName, email: userEmail },
      })
      .then((result) => {
        if (result.error) {
          this.setState({});
          this.setState({ loading: false, error: result.error.message });
        } else {
          this.createServerPaymentIntation(result);
        }
      });
  };

  // RENDERERS

  displayAmount() {
    const { amount } = this.props;

    return numberWithTwoDecimals(amount / 100);
  }

  displayCurrency() {
    const { currency } = this.props;

    return typeof currency === 'string' ? currency : currency.code;
  }

  render() {
    const { description, image } = this.props;
    const { error, loading } = this.state;

    return (
      <form onSubmit={this.handleSubmit}>
        {loading && <Loader fillParent />}
        {description && <p>description</p>}
        {image && <img src={image} className={style.image} />}
        <div className="push" data-testid="stripe-card-element">
          {error && (
            <div className="field_with_errors">
              <span className="error" data-testid="stripe-payment-form-error">
                {error}
              </span>
            </div>
          )}

          <SplitedCardFields />

          <div className="text--center push--top">
            <button
              data-testid="payment-form-submit"
              className="button button--primary button__validation--blue"
              type="submit"
            >
              {__('Pay %{currency} %{amount}').i__({
                currency: this.displayCurrency(),
                amount: this.displayAmount(),
              })}
            </button>
          </div>
        </div>
      </form>
    );
  }
}

PaymentForm.propTypes = {
  stripeAccountId: PropTypes.string.isRequired,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  description: PropTypes.string,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  endpointUrl: PropTypes.string.isRequired,
  endpointData: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  image: PropTypes.string,
  onPaymentSuccess: PropTypes.func,
};

export default PaymentForm;
